import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Plus, X, CheckCircle, Eye } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../services/api';
import LoadingSpinner from '../components/Shared/LoadingSpinner';
import PageHeader from '../components/Shared/PageHeader';
import { fmtTons2, fmtPct, fmtRate, fmtDay, fmtNumber } from '../components/PostCollections/format';
import { c, btn, card, tbl } from '../theme';

// Post Collections Rules — which scale tickets, crews and bales count toward
// each figure on the Daily Productivity Report, per site (migration 103).
//
// The page edits a DRAFT of one site's rules. Nothing reaches the report
// until Save, and Save is only offered once the current draft has been
// previewed: the preview is the report's last two weeks computed with the
// saved rules and with the draft, side by side, so a wrong code shows as a
// wrong number before anyone reads it. Editing after a preview withdraws
// Save until the draft is previewed again.
//
// What this page cannot change, by design: how the figures combine (Total
// Tons Processed, Monday's carry-over, the ratios). That is code, checked
// against the Tableau workbook; see services/postCollectionsMetrics.js.

const DIRECTIONS = [['I', 'Inbound'], ['O', 'Outbound'], ['any', 'In or out']];
const PCT_KEYS = new Set(['pct_processed', 'uptime', 'positive_diversion_pct', 'processed_diversion_pct']);
const LABELS = {
  inbound_tons: 'Inbound tons', inbound_msw: 'Inbound MSW', inbound_comm_organics: 'Comm. organics', inbound_resi_gw: 'Resi GW',
  tons_on_floor: 'Tons on floor', processable_recycle: 'Processable recycle', processable_msw: 'Processable MSW',
  non_processable: 'Non-processable', total_tons_processed: 'Total processed', pct_processed: '% of processable',
  production_hours: 'Production hrs', sorter_hours: 'Sorter hrs', uptime: 'Uptime', scrap_metal: 'Scrap metal',
  glass: 'Glass', aluminum_cans: 'Aluminum cans', metals: 'Metals', occ: 'OCC', mixed_paper: 'Mixed paper',
  plastic: 'Plastic', positive_diversion_tons: 'Positive recovery', positive_diversion_pct: 'Positive %',
  positive_tons_per_sorter_hour: 'Tons / sorter hr', wood: 'Wood', organics_to_crown: 'Organics to Crown',
  fines: 'Fines', total_processed_diversion: 'Total diversion', processed_diversion_pct: 'Diversion %',
};
const fmtKey = (k, v) => (v === null || v === undefined ? '—'
  : PCT_KEYS.has(k) ? fmtPct(v * 100) : k === 'positive_tons_per_sorter_hour' ? fmtRate(v) : fmtTons2(v));

const blankCondition = () => ({
  direction: 'I', all: false, sign: 1, measure: 'tons', skip_sundays: false,
  codes: [], descriptions: [], exclude_codes: [], customers: [], exclude_customers: [],
});
const clone = (o) => JSON.parse(JSON.stringify(o));

export default function PostCollectionsRules() {
  const qc = useQueryClient();
  const overviewQ = useQuery({ queryKey: ['post-collections-rules'], queryFn: () => api.get('/post-collections/rules') });
  const overview = overviewQ.data;
  const [site, setSite] = useState('');
  const current = overview?.sites.find((x) => x.site === site) || overview?.sites[0] || null;
  const code = current?.site || '';

  const [draft, setDraft] = useState(null);           // { rules, on_report }
  const [previewed, setPreviewed] = useState(null);   // { key, result }
  const [note, setNote] = useState('');
  const [failure, setFailure] = useState(null);

  // A fresh draft whenever the site, or what is saved for it, changes.
  useEffect(() => {
    if (!current) return;
    setDraft({ rules: clone(current.rules), on_report: current.on_report });
    setPreviewed(null); setNote(''); setFailure(null);
  }, [current?.site, current?.version]);   // eslint-disable-line react-hooks/exhaustive-deps

  const vocabQ = useQuery({
    queryKey: ['post-collections-rules-vocab', code],
    queryFn: () => api.get(`/post-collections/rules/${code}/vocabulary`),
    enabled: !!code,
  });
  const historyQ = useQuery({
    queryKey: ['post-collections-rules-history', code],
    queryFn: () => api.get(`/post-collections/rules/${code}/history`),
    enabled: !!code,
  });

  const draftKey = draft ? JSON.stringify(draft) : '';
  const savedKey = current ? JSON.stringify({ rules: current.rules, on_report: current.on_report }) : '';
  const dirty = !!draft && draftKey !== savedKey;
  const previewIsCurrent = previewed && previewed.key === draftKey;

  const previewM = useMutation({
    mutationFn: () => api.post(`/post-collections/rules/${code}/preview`, { rules: draft.rules }),
    onMutate: () => setFailure(null),
    onSuccess: (result) => setPreviewed({ key: draftKey, result }),
    onError: (err) => setFailure({ error: err.message, details: err.data?.details || [] }),
  });
  const saveM = useMutation({
    mutationFn: () => api.put(`/post-collections/rules/${code}`, {
      rules: draft.rules, on_report: draft.on_report, version: current.version, note: note || undefined,
    }),
    onSuccess: (r) => {
      toast.success(`Saved ${current.name}'s rules (version ${r.version}). The report uses them now, for every day.`);
      for (const k of ['post-collections-rules', 'post-collections-rules-history', 'post-collections-sites', 'post-collections-report']) {
        qc.invalidateQueries({ queryKey: [k] });
      }
    },
    onError: (err) => setFailure({ error: err.message, details: err.data?.details || [], conflict: err.status === 409 }),
  });

  if (overviewQ.isLoading) return <div style={s.page}><LoadingSpinner /></div>;
  if (overviewQ.error) return <div style={s.page}><div style={s.error} role="alert">{overviewQ.error.message}</div></div>;

  const edit = (fn) => setDraft((d) => { const n = clone(d); fn(n); return n; });

  return (
    <div style={s.page}>
      <PageHeader
        eyebrow="Administration"
        title="Post Collections Rules"
        subtitle="Which scale tickets, crews and bales count toward each figure on the Daily Productivity Report. A change applies to every day the report shows, as a Tableau calculation change does. How the figures add up is fixed and cannot be changed here."
      />

      <div style={s.tabsRow}>
        <div role="group" aria-label="Site" style={s.tabs}>
          {overview.sites.map((x) => (
            <button key={x.site} type="button" aria-pressed={x.site === code} disabled={dirty && x.site !== code}
                    title={dirty && x.site !== code ? 'Save or discard this site’s changes first' : undefined}
                    onClick={() => setSite(x.site)} style={{ ...s.tab, ...(x.site === code ? s.tabOn : null) }}>
              {x.name}{!x.on_report && <span style={s.draftPill}>draft</span>}
            </button>
          ))}
        </div>
        <AddSite overview={overview} disabled={dirty} onAdded={(added) => { setSite(added); qc.invalidateQueries({ queryKey: ['post-collections-rules'] }); }} />
      </div>

      {!current || !draft ? <div style={s.emptyCard}>No site has rules yet. Add one above.</div> : (
        <>
          <section style={s.card}>
            <div style={s.siteBar}>
              <label style={s.check}>
                <input id="rules-on-report" type="checkbox" checked={draft.on_report}
                       onChange={(e) => edit((d) => { d.on_report = e.target.checked; })} />
                Show {current.name} on the report
              </label>
              <span style={s.meta}>
                Version {current.version}{current.updated_by_name ? ` · last saved by ${current.updated_by_name}` : ''}
                {current.updated_at ? ` · ${new Date(current.updated_at).toLocaleString()}` : ''}
              </span>
            </div>
            <label style={s.notesLabel} htmlFor="rules-notes">Notes for whoever edits this next</label>
            <textarea id="rules-notes" rows={2} value={draft.rules.notes}
                      onChange={(e) => { const v = e.target.value; edit((d) => { d.rules.notes = v; }); }} style={s.notes} />
          </section>

          <Figures overview={overview} draft={draft} edit={edit} vocab={vocabQ.data} />

          <div style={s.two}>
            <Sorters draft={draft} edit={edit} vocab={vocabQ.data} />
            <BaleGroups overview={overview} draft={draft} edit={edit} vocab={vocabQ.data} />
          </div>

          <div style={s.bar} role="region" aria-label="Changes">
            <div style={s.barText}>
              {dirty
                ? (previewIsCurrent ? <><strong>Previewed.</strong> Check the numbers below, then save.</>
                  : <><strong>Unsaved changes.</strong> Preview them to see what they do to the report before saving.</>)
                : <><strong>No unsaved changes.</strong> Edit a rule above to preview what it does.</>}
            </div>
            <div style={s.btns}>
              <button type="button" style={{ ...s.secondaryBtn, opacity: dirty ? 1 : 0.5 }} disabled={!dirty || saveM.isPending}
                      onClick={() => { setDraft({ rules: clone(current.rules), on_report: current.on_report }); setPreviewed(null); setFailure(null); }}>
                Discard
              </button>
              <button type="button" style={{ ...s.primaryBtn, opacity: dirty && !previewM.isPending ? 1 : 0.5 }}
                      disabled={!dirty || previewM.isPending} onClick={() => previewM.mutate()}>
                <Eye size={14} /> {previewM.isPending ? 'Previewing…' : 'Preview changes'}
              </button>
            </div>
          </div>

          {failure && (
            <div style={s.errBlock} role="alert">
              <div style={s.errHead}><AlertTriangle size={14} /> {failure.error}</div>
              {failure.details?.length > 0 && <ul style={s.list}>{failure.details.map((d) => <li key={d}>{d}</li>)}</ul>}
              {failure.conflict && (
                <button type="button" style={{ ...s.secondaryBtn, marginTop: '8px' }}
                        onClick={() => qc.invalidateQueries({ queryKey: ['post-collections-rules'] })}>
                  Reload the saved rules
                </button>
              )}
            </div>
          )}

          {dirty && previewIsCurrent && (
            <Preview result={previewed.result} onReport={draft.on_report !== current.on_report ? draft.on_report : null}
                     note={note} setNote={setNote} saving={saveM.isPending} onSave={() => saveM.mutate()} />
          )}

          <History rows={historyQ.data} loading={historyQ.isLoading} />
        </>
      )}

      <p style={s.footnote}>
        Rules apply to the report at <Link to="/post-collections">Post Collections → Daily Productivity Report</Link>.
        Bale weights are uploaded on the <Link to="/post-collections/upload">Post Collections Upload</Link> page.
      </p>
    </div>
  );
}

// ── Figures ──────────────────────────────────────────────────────────────────

function Figures({ overview, draft, edit, vocab }) {
  let lastGroup = null;
  return (
    <section style={s.card} aria-label="What counts toward each figure">
      <div style={s.cardHead}>
        <span>What counts toward each figure</span>
        <small style={s.headSmall}>A ticket counts toward a condition when it matches everything the condition sets. A figure adds up its conditions.</small>
      </div>
      {overview.figures.map((f) => {
        const conds = draft.rules.figures[f.key];
        const showGroup = f.group !== lastGroup; lastGroup = f.group;
        return (
          <div key={f.key}>
            {showGroup && <div style={s.groupLabel}>{f.group}</div>}
            <div style={s.figure}>
              <div style={s.figureHead}>
                <div>
                  <div style={s.figureName}>{f.label}</div>
                  <div style={s.help}>{f.help}{f.optional && !conds.length ? ' Hidden on this site’s report.' : ''}</div>
                </div>
                <button type="button" style={s.linkBtn} aria-label={`Add a condition to ${f.label}`}
                        onClick={() => edit((d) => { d.rules.figures[f.key].push(blankCondition()); })}>
                  <Plus size={13} /> Add condition
                </button>
              </div>
              {!conds.length && <div style={s.none}>No conditions: {f.optional ? 'not shown on this site’s report.' : 'reads 0.'}</div>}
              {conds.map((cond, i) => (
                <Condition key={i} figure={f} index={i} cond={cond} vocab={vocab}
                           set={(fn) => edit((d) => fn(d.rules.figures[f.key][i]))}
                           remove={() => edit((d) => { d.rules.figures[f.key].splice(i, 1); })} />
              ))}
            </div>
          </div>
        );
      })}
    </section>
  );
}

function Condition({ figure, index, cond, vocab, set, remove }) {
  const id = `${figure.key}-${index}`;
  const materials = (vocab?.materials || []).filter((m) => cond.direction === 'any' || m.direction === cond.direction);
  const codeOptions = uniq(materials.map((m) => [m.material_code, m.material_desc]));
  const descOptions = uniq(materials.map((m) => [m.material_desc, m.material_code]));
  const customerOptions = (vocab?.customers || []).map((x) => [x.customer_name, `${fmtNumber(x.tickets)} tickets`]);
  const where = `${figure.label}, condition ${index + 1}`;
  return (
    <div style={s.condition} aria-label={where}>
      <div style={s.condTop}>
        <select id={`${id}-sign`} aria-label={`${where}: add or subtract`} value={cond.sign}
                onChange={(e) => { const v = Number(e.target.value); set((x) => { x.sign = v; }); }} style={s.select}>
          <option value={1}>Add</option><option value={-1}>Subtract</option>
        </select>
        <select id={`${id}-direction`} aria-label={`${where}: direction`} value={cond.direction}
                onChange={(e) => { const v = e.target.value; set((x) => { x.direction = v; }); }} style={s.select}>
          {DIRECTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <span style={s.condWord}>tickets, measured in</span>
        <select id={`${id}-measure`} aria-label={`${where}: measure`} value={cond.measure}
                onChange={(e) => { const v = e.target.value; set((x) => { x.measure = v; }); }} style={s.select}>
          <option value="tons">tons</option><option value="net_lbs">net lbs ÷ 2000</option>
        </select>
        <label style={s.check}>
          <input id={`${id}-all`} type="checkbox" checked={cond.all} onChange={(e) => { const v = e.target.checked; set((x) => { x.all = v; }); }} />
          every material
        </label>
        <label style={s.check}>
          <input id={`${id}-sundays`} type="checkbox" checked={cond.skip_sundays} onChange={(e) => { const v = e.target.checked; set((x) => { x.skip_sundays = v; }); }} />
          skip Sundays
        </label>
        <button type="button" style={{ ...s.linkBtn, color: c.dangerText, marginLeft: 'auto' }} aria-label={`Remove ${where}`} onClick={remove}>
          <X size={13} /> Remove
        </button>
      </div>
      <div style={s.condGrid}>
        {!cond.all && <Chips id={`${id}-codes`} label="Material codes" mono values={cond.codes} options={codeOptions}
                             onChange={(v) => set((x) => { x.codes = v; })} where={where} />}
        {!cond.all && <Chips id={`${id}-descs`} label="Descriptions" values={cond.descriptions} options={descOptions}
                             onChange={(v) => set((x) => { x.descriptions = v; })} where={where} />}
        {cond.all && <Chips id={`${id}-excl`} label="Except material codes" mono values={cond.exclude_codes} options={codeOptions}
                            onChange={(v) => set((x) => { x.exclude_codes = v; })} where={where} />}
        <Chips id={`${id}-cust`} label="Only these customers" values={cond.customers} options={customerOptions} empty="any customer"
               onChange={(v) => set((x) => { x.customers = v; })} where={where} />
        <Chips id={`${id}-xcust`} label="Except these customers" values={cond.exclude_customers} options={customerOptions} empty="none"
               onChange={(v) => set((x) => { x.exclude_customers = v; })} where={where} />
      </div>
      {!cond.all && !cond.codes.length && !cond.descriptions.length && (
        <div style={s.warnInline}><AlertTriangle size={12} /> Matches nothing yet: add a code or description, or tick every material.</div>
      )}
    </div>
  );
}

// A list of values as removable chips, with a field that offers the values
// the site's data actually holds.
function Chips({ id, label, values, options = [], onChange, where, mono = false, empty = 'none', upper = true }) {
  const [text, setText] = useState('');
  const add = () => {
    const v = upper ? text.trim().toUpperCase() : text.trim();
    if (v && !values.some((x) => x.toUpperCase() === v.toUpperCase())) onChange([...values, v]);
    setText('');
  };
  return (
    <div style={s.chipsBlock}>
      <div style={s.chipsLabel}>{label}</div>
      <div style={s.chips}>
        {values.map((v) => (
          <span key={v} style={{ ...s.chip, ...(mono ? s.mono : null) }}>
            {v}
            <button type="button" style={s.chipX} aria-label={`Remove ${v} from ${label} (${where})`}
                    onClick={() => onChange(values.filter((x) => x !== v))}>×</button>
          </span>
        ))}
        {!values.length && <span style={s.chipEmpty}>{empty}</span>}
        <span style={s.addWrap}>
          <input id={`${id}-input`} list={`${id}-list`} value={text} placeholder="add…" aria-label={`Add to ${label} (${where})`}
                 onChange={(e) => setText(e.target.value)}
                 onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} style={{ ...s.addInput, ...(mono ? s.mono : null) }} />
          <button type="button" style={s.addBtn} aria-label={`Add the typed value to ${label} (${where})`} onClick={add} disabled={!text.trim()}>Add</button>
          <datalist id={`${id}-list`}>
            {options.map(([v, hint]) => <option key={v} value={v}>{hint}</option>)}
          </datalist>
        </span>
      </div>
    </div>
  );
}

function uniq(pairs) {
  const m = new Map();
  for (const [v, hint] of pairs) if (v && !m.has(v)) m.set(v, hint || '');
  return [...m.entries()];
}

// ── Sorters and bale groups ──────────────────────────────────────────────────

function Sorters({ draft, edit, vocab }) {
  const seen = (vocab?.lobs || []).map((l) => l.lob);
  const all = [...new Set([...seen, ...draft.rules.sorter_lobs])];
  const stats = new Map((vocab?.lobs || []).map((l) => [l.lob, l]));
  return (
    <section style={s.card}>
      <div style={s.cardHead}><span>Sorter hours: which crews count</span><small style={s.headSmall}>Crews in this site&apos;s data</small></div>
      <div style={s.crews}>
        {!all.length && <div style={s.none}>No crews in this site&apos;s data.</div>}
        {all.map((lob, i) => {
          const on = draft.rules.sorter_lobs.includes(lob);
          const st = stats.get(lob);
          return (
            <label key={lob} style={s.crew}>
              <input id={`sorter-${i}`} type="checkbox" checked={on}
                     onChange={() => edit((d) => {
                       d.rules.sorter_lobs = on ? d.rules.sorter_lobs.filter((x) => x !== lob) : [...d.rules.sorter_lobs, lob];
                     })} />
              {lob}
              <span style={s.crewStat}>{st ? `${fmtNumber(st.days)} days · avg ${fmtTons2(st.avg_hours)} h` : 'not in the data'}</span>
            </label>
          );
        })}
      </div>
    </section>
  );
}

function BaleGroups({ overview, draft, edit, vocab }) {
  const weights = new Map((vocab?.bale_materials || []).map((m) => [m.material_code, m]));
  const options = (vocab?.bale_materials || []).map((m) => [m.material_code, `${m.material_desc || ''} · ${fmtNumber(Math.round(m.weight_per_bale))} lb/bale`]);
  return (
    <section style={s.card}>
      <div style={s.cardHead}><span>Bale weights: which materials make each commodity</span><small style={s.headSmall}>Weighed together: total lbs ÷ total bales</small></div>
      <div style={{ padding: '4px 14px 12px' }}>
        {overview.commodities.map((k) => {
          const mats = draft.rules.bale_groups[k];
          const missing = mats.filter((m) => !weights.has(m));
          return (
            <div key={k} style={s.baleRow}>
              <Chips id={`bale-${k.replace(/\W/g, '')}`} label={k} mono values={mats} options={options}
                     onChange={(v) => edit((d) => { d.rules.bale_groups[k] = v; })} where="bale weights" empty="none: reads 0 tons" />
              {missing.length > 0 && <div style={s.warnInline}><AlertTriangle size={12} /> No weight uploaded for {missing.join(', ')}.</div>}
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ── Preview, history, adding a site ──────────────────────────────────────────

function Preview({ result, onReport, note, setNote, saving, onSave }) {
  const keys = result.changed;
  return (
    <section style={{ ...s.card, borderColor: c.info }} aria-label="Preview">
      <div style={{ ...s.cardHead, background: c.infoTint, color: c.info }}>
        <span>Preview: {result.from ? `${fmtDay(result.from)} – ${fmtDay(result.to)}` : 'no data loaded'}</span>
        <small style={s.headSmall}>Saved rules → your changes. Only the figures that move are shown.</small>
      </div>
      <div style={{ padding: '12px 14px', display: 'grid', gap: '10px' }}>
        {onReport !== null && (
          <div style={s.noteBox}>This also {onReport ? 'adds the site to' : 'removes the site from'} the report.</div>
        )}
        {!keys.length ? (
          <div style={s.none}>No figure changes over these two weeks{result.empty ? ' (nothing is loaded for this site)' : ''}.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={s.table}>
              <thead>
                <tr><th style={s.th}>Day</th>{keys.map((k) => <th key={k} style={s.thR}>{LABELS[k] || k}</th>)}</tr>
              </thead>
              <tbody>
                {result.days.map((d) => (
                  <tr key={d.day} style={tbl.tr}>
                    <td style={s.td}>{fmtDay(d.day, { weekday: true })}</td>
                    {keys.map((k) => <Cell key={k} k={k} before={d.before[k]} after={d.after[k]} />)}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td style={{ ...s.td, fontWeight: 700 }}>Total</td>
                  {keys.map((k) => <Cell key={k} k={k} before={result.total.before[k]} after={result.total.after[k]} strong />)}
                </tr>
              </tfoot>
            </table>
          </div>
        )}
        <div style={s.saveRow}>
          <input id="rules-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="What changed and why (kept in the history)"
                 aria-label="Note for the history" style={s.noteInput} />
          <button type="button" style={{ ...s.primaryBtn, opacity: saving ? 0.5 : 1 }} disabled={saving} onClick={onSave}>
            <CheckCircle size={14} /> {saving ? 'Saving…' : 'Save rules'}
          </button>
        </div>
      </div>
    </section>
  );
}

function Cell({ k, before, after, strong = false }) {
  const same = Math.abs((before ?? 0) - (after ?? 0)) <= 0.005;
  return (
    <td style={{ ...s.tdR, ...(strong ? { fontWeight: 700 } : null) }}>
      {same ? fmtKey(k, after) : (
        <>
          <span style={s.was}>{fmtKey(k, before)}</span>{' → '}
          <span style={{ fontWeight: 600, color: (after ?? 0) > (before ?? 0) ? c.successText : c.dangerText }}>{fmtKey(k, after)}</span>
        </>
      )}
    </td>
  );
}

function History({ rows, loading }) {
  return (
    <section style={s.card}>
      <div style={s.cardHead}><span>Change history</span><small style={s.headSmall}>Every saved version, newest first</small></div>
      {loading ? <LoadingSpinner /> : (
        <ul style={s.history}>
          {(rows || []).map((h) => (
            <li key={h.version} style={s.historyItem}>
              <span style={s.historyWhen}>v{h.version} · {new Date(h.changed_at).toLocaleString()}</span>
              <span>
                <strong>{h.changed_by_name || 'Setup'}</strong> {h.action === 'created' ? 'created these rules' : 'saved a change'}
                {h.on_report ? '' : ' (off the report)'}{h.note ? `: ${h.note}` : '.'}
              </span>
            </li>
          ))}
          {!rows?.length && <li style={s.historyItem}>No saved versions.</li>}
        </ul>
      )}
    </section>
  );
}

function AddSite({ overview, disabled, onAdded }) {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [from, setFrom] = useState('');
  const m = useMutation({
    mutationFn: () => api.post('/post-collections/rules', { site: code, copy_from: from || null }),
    onSuccess: (r) => { toast.success('Site added, off the report until you switch it on.'); setOpen(false); setCode(''); setFrom(''); onAdded(r.site); },
    onError: (err) => toast.error(err.message || 'Could not add the site.'),
  });
  const choices = useMemo(() => overview.addable || [], [overview]);
  if (!choices.length) return null;
  if (!open) {
    return (
      <button type="button" style={{ ...s.secondaryBtn, opacity: disabled ? 0.5 : 1 }} disabled={disabled} onClick={() => setOpen(true)}>
        <Plus size={14} /> Add site
      </button>
    );
  }
  return (
    <div style={s.addSite}>
      <select id="add-site-code" aria-label="Site to add" value={code} onChange={(e) => setCode(e.target.value)} style={s.select}>
        <option value="">Choose a site…</option>
        {choices.map((x) => <option key={x.code} value={x.code}>{x.name} ({x.code})</option>)}
      </select>
      <select id="add-site-from" aria-label="Copy rules from" value={from} onChange={(e) => setFrom(e.target.value)} style={s.select}>
        <option value="">Start empty</option>
        {overview.sites.map((x) => <option key={x.site} value={x.site}>Copy {x.name}</option>)}
      </select>
      <button type="button" style={{ ...s.primaryBtn, opacity: code ? 1 : 0.5 }} disabled={!code || m.isPending} onClick={() => m.mutate()}>Add</button>
      <button type="button" style={s.linkBtn} onClick={() => setOpen(false)}>Cancel</button>
    </div>
  );
}

const s = {
  page:        { maxWidth: '1180px', margin: '0 auto', paddingBottom: '8px' },
  tabsRow:     { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '14px' },
  tabs:        { display: 'flex', border: `1px solid ${c.border}`, borderRadius: '8px', overflow: 'hidden', background: c.surface, flexWrap: 'wrap' },
  tab:         { border: 'none', borderRight: `1px solid ${c.border}`, background: c.surface, color: c.textStrong, fontSize: '12.5px', fontFamily: 'inherit', padding: '8px 13px', cursor: 'pointer', display: 'inline-flex', gap: '6px', alignItems: 'center' },
  tabOn:       { background: c.navy, color: c.onNavy },
  draftPill:   { fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px', background: c.warningTint, color: c.warningText, borderRadius: '8px', padding: '1px 6px' },
  card:        { ...card.sm, overflow: 'hidden', marginBottom: '14px', border: `1px solid ${c.border}` },
  cardHead:    { ...card.head, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap', padding: '11px 14px', borderBottom: `1px solid ${c.border}`, background: c.surfaceSunk },
  headSmall:   { fontSize: '11.5px', fontWeight: 400, color: c.textMuted },
  siteBar:     { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap', padding: '12px 14px 6px' },
  meta:        { fontSize: '11.5px', color: c.textMuted },
  notesLabel:  { display: 'block', fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: c.textMuted, padding: '4px 14px' },
  notes:       { display: 'block', width: 'calc(100% - 28px)', margin: '0 14px 14px', fontFamily: 'inherit', fontSize: '12.5px', color: c.text, background: c.surface, border: `1px solid ${c.border}`, borderRadius: '6px', padding: '7px 9px', resize: 'vertical' },
  groupLabel:  { fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.6px', color: c.textFaint, padding: '12px 14px 0' },
  figure:      { padding: '10px 14px 12px', borderBottom: `1px solid ${c.canvas}`, display: 'grid', gap: '8px' },
  figureHead:  { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px' },
  figureName:  { fontSize: '13px', fontWeight: 600, color: c.text },
  help:        { fontSize: '11.5px', color: c.textMuted },
  condition:   { ...card.sunk, padding: '9px 11px', display: 'grid', gap: '8px' },
  condTop:     { display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' },
  condWord:    { fontSize: '12px', color: c.textMuted },
  condGrid:    { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '8px 14px' },
  select:      { height: '28px', fontSize: '12px', fontFamily: 'inherit', padding: '0 6px', border: `1px solid ${c.border}`, borderRadius: '6px', background: c.surface, color: c.text },
  check:       { display: 'inline-flex', gap: '6px', alignItems: 'center', fontSize: '12px', color: c.textStrong },
  chipsBlock:  { display: 'grid', gap: '4px', minWidth: 0 },
  chipsLabel:  { fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: c.textMuted },
  chips:       { display: 'flex', flexWrap: 'wrap', gap: '5px', alignItems: 'center' },
  chip:        { display: 'inline-flex', alignItems: 'center', gap: '2px', background: c.infoTint, color: c.textStrong, borderRadius: '6px', padding: '2px 3px 2px 7px', fontSize: '11.5px' },
  mono:        { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' },
  chipX:       { border: 'none', background: 'transparent', color: c.textMuted, cursor: 'pointer', fontSize: '13px', lineHeight: 1, padding: '0 4px' },
  chipEmpty:   { fontSize: '11.5px', color: c.textFaint },
  addWrap:     { display: 'inline-flex', gap: '4px' },
  addInput:    { width: '130px', height: '24px', fontSize: '11.5px', fontFamily: 'inherit', border: `1px dashed ${c.borderStrong}`, borderRadius: '6px', padding: '0 6px', background: c.surface, color: c.text },
  addBtn:      { ...btn.secondary, height: '24px', padding: '0 8px', fontSize: '11px' },
  warnInline:  { display: 'flex', gap: '5px', alignItems: 'center', fontSize: '11.5px', color: c.warningText },
  linkBtn:     { ...btn.link, display: 'inline-flex', gap: '4px', alignItems: 'center', fontSize: '12px', whiteSpace: 'nowrap' },
  none:        { fontSize: '12px', color: c.textFaint },
  two:         { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '14px', alignItems: 'start' },
  crews:       { padding: '10px 14px 12px', display: 'grid', gap: '6px' },
  crew:        { display: 'flex', gap: '8px', alignItems: 'center', fontSize: '12.5px', color: c.textStrong },
  crewStat:    { marginLeft: 'auto', fontSize: '11.5px', color: c.textFaint, fontVariantNumeric: 'tabular-nums' },
  baleRow:     { padding: '8px 0', borderBottom: `1px solid ${c.canvas}`, display: 'grid', gap: '4px' },
  bar:         { ...card.sm, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap', padding: '11px 14px', marginBottom: '14px', position: 'sticky', bottom: '8px', zIndex: 2, boxShadow: '0 6px 18px -10px rgba(17,24,39,.35)' },
  barText:     { fontSize: '12.5px', color: c.textMuted },
  btns:        { display: 'flex', gap: '8px' },
  primaryBtn:  { ...btn.primary, display: 'inline-flex', gap: '6px', alignItems: 'center' },
  secondaryBtn:{ ...btn.secondary, display: 'inline-flex', gap: '6px', alignItems: 'center' },
  errBlock:    { background: c.dangerTint, border: '1px solid #FCA5A5', borderRadius: '10px', padding: '12px 14px', marginBottom: '14px', color: c.dangerText, fontSize: '12px' },
  errHead:     { display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 600 },
  list:        { margin: '6px 0 0', paddingLeft: '18px', lineHeight: 1.6 },
  table:       { width: '100%', borderCollapse: 'collapse', fontSize: '12px' },
  th:          { ...tbl.thSm, whiteSpace: 'nowrap' },
  thR:         { ...tbl.thRSm, whiteSpace: 'nowrap' },
  td:          { ...tbl.tdSm, whiteSpace: 'nowrap' },
  tdR:         tbl.tdRSm,
  was:         { color: c.textFaint, textDecoration: 'line-through' },
  noteBox:     { background: c.warningTint, color: c.warningText, borderRadius: '8px', padding: '8px 10px', fontSize: '12px' },
  saveRow:     { display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' },
  noteInput:   { flex: '1 1 260px', height: '32px', fontSize: '12.5px', fontFamily: 'inherit', border: `1px solid ${c.border}`, borderRadius: '6px', padding: '0 9px', background: c.surface, color: c.text },
  history:     { listStyle: 'none', margin: 0, padding: 0 },
  historyItem: { display: 'grid', gridTemplateColumns: '220px minmax(0, 1fr)', gap: '10px', padding: '8px 14px', borderBottom: `1px solid ${c.canvas}`, fontSize: '12.5px', color: c.textStrong },
  historyWhen: { color: c.textMuted, fontVariantNumeric: 'tabular-nums' },
  addSite:     { display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' },
  footnote:    { fontSize: '11.5px', color: c.textMuted, margin: '0 0 14px' },
  emptyCard:   { ...card.sm, padding: '40px 24px', textAlign: 'center', color: c.textMuted, fontSize: '13px' },
  error:       { padding: '16px', color: c.dangerText, fontSize: '12.5px' },
};
